import React, { useState, useContext, createContext, PropsWithChildren, useMemo, useRef } from 'react';
import { ErrorModal, errorModal } from 'vl-common/src/components/Modals/CustomModals/Error';
import { SuccessModal, successModal } from 'vl-common/src/components/Modals';

import { AddNote } from 'vl-common/src/components/Appointments/AddNoteModal';
import { ViewNotesModal } from 'vl-common/src/components/Appointments/ViewNotesModal';
import { Case } from '../../schemas/shared';
import useApi from '../../hooks/useApi';
import { ChangeCaseRefModal } from '../../components/ChangeCaseRefModal/ChangeCaseRefModal';
import { AddUbrn } from 'vl-common/src/components/Cases/AddUbrn';
import RemoveQuotaModal from 'vl-common/src/components/Cases/RemoveQuotaModal';
import AddQuotaModal from 'vl-common/src/components/Cases/AddQuotaModal';
import { DischargeCase } from '../../components/DischargeCase/DischargeCase';
import { ChangePriorityModal } from 'vl-common/src/components/Cases/ChangePriorityModal';
import { useQuery } from '@tanstack/react-query';
import useRouter from 'vl-core/useRouter';
import { useBookingFlowControl } from '../../hooks/useBookingFlowControl';
import { getCurrentLocation } from '../getCurrentLocation';
import { CaseReviewApptTypeList } from '../../hooks/useAppointmentManagmentActions';
import ChooseAppointmentTypeModal from '../../components/BookAppointment/ChooseAppointmentTypeModal';
import useAdminPageURL from '../../hooks/useAdminPageURL';
import AdditionalPatientInfo from '../../components/Appointments/AdditionalPatientInfo';
import { useEnsurePatientById } from '../../hooks/usePatientByIdQuery';
import { PatientType } from './ManagePatientContext';

type CaseManagementContextType = {
  onSetActionedCase: (params: { activeCase: Case; refetchFn?: () => void }) => void;
  openChangeCaseRefModal: () => void;
  openAddNotesModal: () => void;
  openViewNotesModal: () => void;
  openAddUbrnModal: () => void;
  openRemoveQuotaModal: () => void;
  openAddQuotaModal: () => void;
  openDischargeModal: () => void;
  openChangePriorityModal: () => void;
  onCaseReopen: (case_id: number) => void;
  onBookCaseAppointment: (activeCase: Case) => void;
};

const CaseManagementContext = createContext<CaseManagementContextType | null>(null);

const CaseManagementProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { Api, consumed, toasted } = useApi();
  const router = useRouter();
  const ensurePatientById = useEnsurePatientById();

  const [actionedCase, setActionedCase] = useState<Case | null>(null);

  const [showChangeCaseRefModal, setShowChangeCaseRefModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showViewNotes, setShowViewNotes] = useState(false);
  const [showAddUbrnModal, setShowAddUbrnModal] = useState(false);
  const [showRemoveQuotaModal, setShowRemoveQuotaModal] = useState(false);
  const [showAddQuotaModal, setShowAddQuotaModal] = useState(false);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [showChangePriorityModal, setShowChangePriorityModal] = useState(false);
  const [showSelectApptTypeModal, setShowSelectApptTypeModal] = useState(false);
  const [showAdditionalPatientInfoModal, setShowAdditionalPatientInfoModal] = useState(false);

  const [showCaseReopenSuccessModal, setShowCaseReopenSuccessModal] = useState(false);
  const [caseReopenErrorModalState, setCaseReopenErrorModalState] = useState({ open: false, reason: null });

  const { initializeNewAppointmentBooking, initializeCaseReviewSchedule } = useBookingFlowControl();

  const { getAppointmentBookingPageURL, getScheduleCaseReviewPageURL } = useAdminPageURL();

  const closeCaseReopenSuccessModal = () => {
    setActionedCase(null);
    setShowCaseReopenSuccessModal(false);
  };
  const closeCaseReopenErrorModal = () => {
    setActionedCase(null);
    setCaseReopenErrorModalState({ open: false, reason: null });
  };

  const getCasePriorities = async () => {
    const res = await Api.meta.getUsersCaseMeta(actionedCase?.case_id).catch(toasted);
    return {
      casePriorities: res.data.meta[0].clinical_urgencies,
      urgencyChangeReasons: res.data.meta[0].urgency_change_reasons
    };
  };

  const { data: { casePriorities, urgencyChangeReasons } = { casePriorities: [], urgencyChangeReasons: [] } } =
    useQuery({
      queryKey: ['casesUrgency', actionedCase?.case_id],
      queryFn: getCasePriorities,
      enabled: !!actionedCase?.case_id
    });

  const [bookingPatient, setBookingPatient] = useState<PatientType | null>(null);

  const onCaseUpdateHandler = useRef<(() => void) | null>(null);

  const withResetActionedCase = (fn: () => void) => () => {
    fn();
    setActionedCase(null);
  };

  const openChangeCaseRefModal = () => setShowChangeCaseRefModal(true);
  const openAddNotesModal = () => setShowAddNoteModal(true);
  const openViewNotesModal = () => setShowViewNotes(true);
  const openAddUbrnModal = () => setShowAddUbrnModal(true);
  const openRemoveQuotaModal = () => setShowRemoveQuotaModal(true);
  const openAddQuotaModal = () => setShowAddQuotaModal(true);
  const openDischargeModal = () => setShowDischargeModal(true);
  const openChangePriorityModal = () => setShowChangePriorityModal(true);
  const openSelectApptTypeModal = () => setShowSelectApptTypeModal(true);

  const closeChangeCaseRefModal = withResetActionedCase(() => setShowChangeCaseRefModal(false));
  const closeAddNotesModal = withResetActionedCase(() => setShowAddNoteModal(false));
  const closeViewNotesModal = withResetActionedCase(() => setShowViewNotes(false));
  const closeAddUbrnModal = withResetActionedCase(() => setShowAddUbrnModal(false));
  const closeRemoveQuotaModal = withResetActionedCase(() => setShowRemoveQuotaModal(false));
  const closeAddQuotaModal = withResetActionedCase(() => setShowAddQuotaModal(false));
  const closeDischargeModal = withResetActionedCase(() => setShowDischargeModal(false));
  const closeChangePriorityModal = withResetActionedCase(() => setShowChangePriorityModal(false));
  const closeSelectApptTypeModal = () => setShowSelectApptTypeModal(false);

  const onChangeCaseRefSuccess = () => {
    closeChangeCaseRefModal();
    onCaseUpdateHandler?.current?.();
  };

  const onRemoveQuotaSuccess = () => {
    closeRemoveQuotaModal();
    onCaseUpdateHandler?.current?.();
  };

  const onAddQuotaSuccess = () => {
    closeAddQuotaModal();
    onCaseUpdateHandler?.current?.();
  };

  const onChangePrioritySuccess = () => {
    closeChangePriorityModal();
    onCaseUpdateHandler?.current?.();
  };

  const onCaseReopen = async (case_id: number) => {
    const appt_post_res = await Api.cases
      .postReopenCase({
        case_id
      })
      .catch((e) => {
        setCaseReopenErrorModalState({ open: true, reason: e?.response?.data?.message || 'Unable to re-open case.' });
        consumed(e);
      });
    if (appt_post_res) {
      setShowCaseReopenSuccessModal(true);
      onCaseUpdateHandler?.current?.();
    }
  };

  const onCaseDischarge = (dischargeReason: { key: string }) => {
    const { key } = dischargeReason;
    const { appointment_id: appt_id, case_id, client_case_id } = actionedCase as Case;
    Api.cases
      .postDischargeCase({
        appt_id,
        case_id,
        discharge_code: key
      })
      .then((_res) => {
        onCaseUpdateHandler?.current?.();
        successModal({ title: `Discharged Case: ${client_case_id}` });
      })
      .catch((error) => {
        consumed(error);
        type StatusCode = 417 | 425;
        const backendMessage = error?.response?.data?.message;
        const customMessages: Record<StatusCode, string> = {
          417: 'Unable to discharge case with outstanding appointments',
          425: 'Unable to discharge case with outstanding requests'
        };
        const status_code = error?.response?.status.toString() as StatusCode;
        const message = customMessages[status_code] || backendMessage || 'An error occurred';
        errorModal({ title: `Failed to Discharge Case: ${client_case_id}`, content: message });
      })
      .finally(() => {
        closeDischargeModal();
      });
  };

  const onSetActionedCase = ({ activeCase, refetchFn }: { activeCase: Case; refetchFn?: () => void }) => {
    setActionedCase(activeCase);

    if (refetchFn) {
      onCaseUpdateHandler.current = refetchFn;
    }
  };

  const onBookCaseAppointment = async (activeCase: Case) => {
    try {
      const patient = await ensurePatientById(activeCase.patient_user_guid);
      setBookingPatient(patient);

      const { postcode, dob } = patient ?? {};

      if (!postcode || !dob) {
        setShowAdditionalPatientInfoModal(true);
        return;
      }

      openSelectApptTypeModal();
    } catch (e) {
      toasted(e);
    }
  };

  const proceedToBooking = (apptType: string, amendmentReason?: string, additionalInfo?: string) => {
    if (!actionedCase) {
      throw new Error('Missing case data');
    }

    const isCaseReview = CaseReviewApptTypeList.includes(apptType);
    const { pathname, search } = getCurrentLocation();

    const currentPageLocation = `${pathname}${search}`;
    const bookingPageURL = getAppointmentBookingPageURL();
    const scheduleCaseReviewPageURL = getScheduleCaseReviewPageURL();

    if (isCaseReview) {
      initializeCaseReviewSchedule({
        patientId: actionedCase.patient_user_guid,
        caseId: actionedCase.case_id,
        apptType,
        reopen_code: amendmentReason,
        reopen_info: additionalInfo,
        redirectRoute: currentPageLocation
      });

      router.push(scheduleCaseReviewPageURL);
      return;
    }

    initializeNewAppointmentBooking({
      patientId: actionedCase.patient_user_guid,
      caseId: actionedCase.case_id,
      apptType,
      redirectRoute: currentPageLocation,
      ...(amendmentReason && { amendmentReason, additionalInfo })
    });

    router.push(bookingPageURL);
  };

  const contextValue = useMemo(() => {
    return {
      onSetActionedCase,
      openChangeCaseRefModal,
      openAddNotesModal,
      openViewNotesModal,
      openAddUbrnModal,
      openRemoveQuotaModal,
      openAddQuotaModal,
      openDischargeModal,
      openChangePriorityModal,
      onCaseReopen,
      onBookCaseAppointment
    };
  }, [actionedCase]);

  return (
    <CaseManagementContext.Provider value={contextValue}>
      {children}

      {actionedCase && (
        <>
          <ChangeCaseRefModal
            isOpen={showChangeCaseRefModal}
            currentRef={actionedCase.client_case_id}
            case_id={actionedCase.case_id}
            noBtnHandler={closeChangeCaseRefModal}
            confirmHandler={onChangeCaseRefSuccess}
          />

          <AddNote
            close={closeAddNotesModal}
            show={showAddNoteModal}
            onCreated={closeAddNotesModal}
            isAddMode={true}
            data={actionedCase}
          />

          <ViewNotesModal
            show={showViewNotes}
            close={closeViewNotesModal}
            data={actionedCase}
            getNotesParams={{
              case_id: actionedCase.case_id
            }}
          />

          <AddUbrn
            close={closeAddUbrnModal}
            show={showAddUbrnModal}
            onCreated={closeAddUbrnModal}
            data={actionedCase}
          />

          <RemoveQuotaModal
            onCancel={closeRemoveQuotaModal}
            open={showRemoveQuotaModal}
            onCreated={onRemoveQuotaSuccess}
            caseId={actionedCase.case_id}
            clientCaseId={actionedCase.client_case_id}
          />

          <AddQuotaModal
            onCancel={closeAddQuotaModal}
            open={showAddQuotaModal}
            onCreated={onAddQuotaSuccess}
            caseId={actionedCase.case_id}
          />

          <DischargeCase
            caseId={actionedCase.case_id}
            isOpen={showDischargeModal}
            toggle={closeDischargeModal}
            noBtnHandler={closeDischargeModal}
            yesBtnHandler={onCaseDischarge}
            yesBtnText="Confirm"
            noBtnText="Cancel"
            subText="Please select a reason why you are discharging the case"
          />

          <ChangePriorityModal
            prioritiesList={casePriorities}
            urgencyChangeReasons={urgencyChangeReasons}
            modalLocation="cases"
            // @ts-expect-error
            caseId={actionedCase.case_id}
            isOpen={showChangePriorityModal}
            toggle={closeChangePriorityModal}
            noBtnHandler={closeChangePriorityModal}
            yesBtnHandler={onChangePrioritySuccess}
          />

          <ChooseAppointmentTypeModal
            key={Number(showSelectApptTypeModal)}
            isOpen={showSelectApptTypeModal}
            toggle={closeSelectApptTypeModal}
            onSelectAppointmentType={(apptType: string, amendmentReason?: string, additionalInfo?: string) => {
              proceedToBooking(apptType, amendmentReason, additionalInfo);
            }}
            onCancel={closeSelectApptTypeModal}
            caseId={actionedCase.case_id}
          />

          <AdditionalPatientInfo
            key={`additional-info-${Number(showAdditionalPatientInfoModal)}`}
            show={showAdditionalPatientInfoModal}
            selectedPatient={bookingPatient}
            close={(cancelled?: boolean) => {
              setShowAdditionalPatientInfoModal(false);
              if (!cancelled) {
                openSelectApptTypeModal();
              }
            }}
          />
        </>
      )}

      <SuccessModal
        title="Case re-opened successfully"
        open={showCaseReopenSuccessModal}
        onCancel={closeCaseReopenSuccessModal}
        cancelText="Ok"
        data-testid="case-re-opened-successfully-modal"
      />

      <ErrorModal
        open={caseReopenErrorModalState.open}
        title="Cannot re-open case"
        subTitle={`Reason: ${caseReopenErrorModalState.reason}`}
        onCancel={closeCaseReopenErrorModal}
        cancelText="Ok"
        data-testid="case-re-open-error-modal"
      />
    </CaseManagementContext.Provider>
  );
};

export const useManageCaseContext = () => {
  const ctx = useContext(CaseManagementContext);

  if (!ctx) {
    throw new Error('useManageCaseContext must be used inside <CaseManagementProvider>');
  }

  return ctx;
};

export default CaseManagementProvider;
